import {
    enumValue,
    getGapTypeBySection,
    gapIsAddressed,
    isInRolledUpStatus,
    enums,
    convertPagination,
    healthHxTabs,
} from '~/util';

class PatientService {
    static EVENTS = {
        patient_updated: 'PatientService.patientUpdated',
        notes_updated: 'PatientService.notesUpdated',
        reminders_updated: 'PatientService.remindersUpdated',
        uploads_updated: 'PatientService.uploadsUpdated',
        risk_gap_response: 'PatientService.riskGapResponse',
        timely_action_counts_updated: 'PatientService.timelyActionCountsUpdated',
    };

    constructor(props) {
        this.props = {
            api: props.api,
            events: props.events,
        };
        this.props.events.init(PatientService.EVENTS);
        this.state = {
            patient: {},
        };
    }

    get(id) {
        return this.state.patient[id];
    }

    async load(id, catchMissing) {
        const options = {};
        if (catchMissing) options.handledErrors = { 404: true };
        const result = await this.props.api.call(
            {
                method: 'get',
                url: `/api/patients/${id}`,
            },
            options
        );
        result.data.risk_gaps = result.data.risk_gaps.map((x) =>
            PatientService.setRiskGapAnswer(x)
        );
        this.state.patient[id] = result.data;
        this.props.events.publish(PatientService.EVENTS.patient_updated);
    }

    static setRiskGapAnswer(gap) {
        gap.uiAnswer = null;
        if (gap?.submission_answer?.answer) {
            if (gap.submission_answer.answer === 1) gap.uiAnswer = true;
            if (gap.submission_answer.answer === 2) gap.uiAnswer = false;
        }
        return gap;
    }

    async update(id, params) {
        const result = await this.props.api.call({
            method: 'put',
            url: `/api/patients/${id}`,
            data: params,
        });
        result.data.risk_gaps = result.data.risk_gaps.map((x) =>
            PatientService.setRiskGapAnswer(x)
        );
        this.state.patient[id] = result.data;
        this.props.events.publish(PatientService.EVENTS.patient_updated);

        return result.data;
    }

    stats(id) {
        const statMap = {
            foc_gaps: [
                enumValue(['gap', 'status', 'Pending']),
                enumValue(['gap', 'status', 'Addressed']),
                enumValue(['gap', 'status', 'Under Review']),
            ],
            quality_gaps: [
                enumValue(['gap', 'status', 'Pending']),
                enumValue(['gap', 'status', 'Addressed']),
                enumValue(['gap', 'status', 'Fail']),
                enumValue(['gap', 'status', 'Under Review']),
            ],
            risk_gaps: [
                enumValue(['gap', 'status', 'Pending']),
                enumValue(['gap', 'status', 'Addressed']),
                enumValue(['gap', 'status', 'Fail']),
                enumValue(['gap', 'status', 'Under Review']),
            ],
        };
        const stats = {};
        Object.keys(statMap).forEach((stat) => {
            stats[stat] = statMap[stat].map((x) => {
                const xInt = parseInt(x);
                return {
                    status: xInt,
                    count: this.state.patient[id][stat].filter((y) => {
                        return (
                            isInRolledUpStatus(getGapTypeBySection(stat), xInt, y.status) &&
                            !y.is_added
                        );
                    }).length,
                };
            });
        });
        // If there are siblings, we count one for each unique status of the siblings.
        // If the primary is addressed, all siblings will also be addressed, so we
        // have already counted what we need.
        // If the primary is any other status, the siblings will all be addressed, so
        // we can just increment addressed by one for each case of siblings exist and
        // primary gap status is not addressed.
        stats.risk_gaps.find((x) => x.status === enumValue(['gap', 'status', 'Addressed'])).count +=
            this.state.patient[id].risk_gaps.filter(
                (x) => x?.siblings.length > 0 && !gapIsAddressed(x)
            ).length;
        // Get the sum, but remember that Failed is already added in Pending, so don't
        // count it twice!
        const riskGapTotal = stats.risk_gaps.reduce(
            (acc, x) => (x.status === enumValue(['gap', 'status', 'Fail']) ? acc : acc + x.count),
            0
        );
        stats.risk_gaps.push({
            status: 'percent',
            count:
                riskGapTotal === 0
                    ? '0%'
                    : `${Math.round(
                          (stats.risk_gaps.find(
                              (x) => x.status === enumValue(['gap', 'status', 'Addressed'])
                          ).count /
                              riskGapTotal) *
                              100
                      )}%`,
        });
        return stats;
    }

    async addRiskGap(patientId, params) {
        params.patient = { id: patientId };
        params.submission_answer = { answer: 1 };
        const result = await this.props.api.call({
            method: 'post',
            url: '/api/risk-gaps',
            data: params,
        });
        this.state.patient[patientId].risk_gaps.push(PatientService.setRiskGapAnswer(result.data));
        this.props.events.publish(PatientService.EVENTS.patient_updated);
        this.props.events.publish(PatientService.EVENTS.notes_updated);

        return result.data;
    }

    async updateRiskGap(id, patientId, params) {
        const result = await this.props.api.call({
            method: 'put',
            url: `/api/risk-gaps/${id}`,
            data: params,
        });

        if (params.notes) {
            this.props.events.publish(PatientService.EVENTS.notes_updated);
        }
        if (params.tasks) {
            this.props.events.publish(PatientService.EVENTS.reminders_updated);
        }
        if (patientId) {
            this.state.patient[patientId].risk_gaps.forEach((x, i, a) => {
                if (x.id === id) {
                    a[i] = PatientService.setRiskGapAnswer(result.data);
                }
            });
            this.props.events.publish(PatientService.EVENTS.patient_updated);
        }
    }

    async updateFocGap(id, patientId, params, options) {
        const result = await this.props.api.call(
            {
                method: 'put',
                url: `/api/foc-gaps/${id}`,
                data: params,
            },
            options
        );
        if (params.notes) {
            this.props.events.publish(PatientService.EVENTS.notes_updated);
        }
        if (params.tasks) {
            this.props.events.publish(PatientService.EVENTS.reminders_updated);
        }
        if (patientId) {
            this.state.patient[patientId].foc_gaps.forEach((x, i, a) => {
                if (x.id === id) {
                    a[i] = result.data;
                }
            });
            this.props.events.publish(PatientService.EVENTS.patient_updated);
        }
    }

    async updateQualityGap(id, patientId, params, options) {
        const result = await this.props.api.call(
            {
                method: 'put',
                url: `/api/quality-gaps/${id}`,
                data: params,
            },
            options
        );
        if (params.notes) {
            this.props.events.publish(PatientService.EVENTS.notes_updated);
        }
        if (params.tasks) {
            this.props.events.publish(PatientService.EVENTS.reminders_updated);
        }
        if (patientId) {
            this.state.patient[patientId].quality_gaps.forEach((x, i, a) => {
                if (x.id === id) {
                    a[i] = result.data;
                }
            });
            this.props.events.publish(PatientService.EVENTS.patient_updated);
        }
    }

    storeSubmission(patientId, submission) {
        if (this.state.patient[patientId].submissions.some((x) => x.id === submission.id)) {
            this.state.patient[patientId].submissions = this.state.patient[
                patientId
            ].submissions.map((x) => {
                if (x.id === submission.id) {
                    return submission;
                }
                return x;
            });
            this.props.events.publish(PatientService.EVENTS.uploads_updated);
        } else {
            this.state.patient[patientId].submissions.push(submission);
            this.props.events.publish(PatientService.EVENTS.patient_updated);
        }
    }

    async addSubmission(params) {
        const result = await this.props.api.call({
            method: 'post',
            url: '/api/submissions',
            data: params,
        });
        this.storeSubmission(params.patient.id, result.data);

        return result.data;
    }

    async submitSubmission(params) {
        const result = await this.props.api.call({
            method: 'post',
            url: `/api/submissions/submit`,
            data: params,
        });

        this.state.patient[params.patient.id].submissions = this.state.patient[
            params.patient.id
        ].submissions.filter((x) => x.id !== params.id);
        this.props.events.publish(PatientService.EVENTS.uploads_updated);
        this.props.events.publish(PatientService.EVENTS.notes_updated);

        return result.data;
    }

    async updateSubmission(id, params) {
        const result = await this.props.api.call({
            method: 'put',
            url: `/api/submissions/${id}`,
            data: params,
        });
        this.storeSubmission(params.patient.id, result.data);

        return result.data;
    }

    async getGap(gapId, gapType) {
        let url = '';
        let gapProp = '';

        switch (gapType) {
            case enumValue(['gap', 'type', 'Risk Gap']):
                url = '/api/risk-gaps/';
                gapProp = 'risk_gaps';
                break;
            case enumValue(['gap', 'type', 'FOC Gap']):
                url = '/api/foc-gaps/';
                gapProp = 'foc_gaps';
                break;
            case enumValue(['gap', 'type', 'Quality Gap']):
                url = '/api/quality-gaps/';
                gapProp = 'quality_gaps';
                break;
            default:
                return;
        }

        const result = await this.props.api.call({
            method: 'get',
            url: url + gapId,
        });
        this.state.patient[result.data.patient?.split('/')[3]]?.[gapProp].forEach((x, i, a) => {
            if (x.id === gapId) {
                a[i] = PatientService.setRiskGapAnswer(result.data);
            }
        });
        this.props.events.publish(PatientService.EVENTS.patient_updated);
    }

    async getNotes(id, type, actions) {
        let route = `/api/notes?${type}=${id}`;
        if (typeof actions !== 'undefined' && actions !== null) {
            if (Array.isArray(actions)) {
                actions.forEach((x) => {
                    route += `&action[]=${x}`;
                });
            } else if (actions === true) {
                route += '&exists[action]=true';
            } else {
                route += '&exists[action]=false';
                // Omit task notes from any other notes feed
                if (type !== 'task.id') {
                    route += Object.keys(enums(['note', 'type']))
                        .filter((x) => parseInt(x) !== enumValue(['note', 'type', 'Task Note']))
                        .map((x) => `&type[]=${x}`)
                        .join('');
                }
            }
        }
        const result = await this.props.api.call({
            method: 'get',
            url: route,
        });
        return result.data;
    }

    async removeRiskGap(id, patientId) {
        await this.props.api.call({
            method: 'delete',
            url: `/api/risk-gaps/${id}`,
        });
        this.state.patient[patientId].risk_gaps = this.state.patient[patientId].risk_gaps.filter(
            (x) => x.id !== id
        );
        this.props.events.publish(PatientService.EVENTS.notes_updated);
        this.props.events.publish(PatientService.EVENTS.patient_updated);
    }

    async riskGapResponse(gapId, answer, updatedAt, options) {
        const result = await this.props.api.call(
            {
                method: 'put',
                url: `/api/risk-gaps/${gapId}`,
                data: {
                    submission_answer: {
                        answer: answer === true ? 1 : 2,
                    },
                    updated_at: updatedAt,
                },
            },
            options
        );
        this.state.patient[result.data.patient.split('/')[3]].risk_gaps.forEach((x, i, a) => {
            if (x.id === gapId) {
                a[i] = PatientService.setRiskGapAnswer(result.data);
            }
        });
        this.props.events.publish(PatientService.EVENTS.risk_gap_response);
        this.props.events.publish(PatientService.EVENTS.patient_updated);
    }

    async deleteSubmissionAnswer(gapType, gapId, submissionAnswerId) {
        await this.props.api.call({
            method: 'delete',
            url: `/api/submission-answers/${submissionAnswerId}`,
        });
        await this.getGap(gapId, gapType);
    }

    async loadSubmission(submissionId) {
        const result = await this.props.api.call({
            method: 'get',
            url: `/api/submissions/${submissionId}`,
        });
        return result.data;
    }

    async uploadMedia(patientId, gapType, files, isMIC) {
        const data = new FormData();
        data.set(
            'data',
            JSON.stringify({
                patient: `/api/patients/${patientId}`,
                gapType,
            })
        );
        files.forEach((file, index) => data.append(`file${index + 1}`, file));
        const headers = {};
        if (isMIC) headers['x-mic-action'] = true;
        const result = await this.props.api.call({
            method: 'post',
            url: '/api/submissions',
            data,
            headers,
        });
        this.storeSubmission(patientId, result.data);
        this.props.events.publish(PatientService.EVENTS.notes_updated);
    }

    async previewMedia(mediaId) {
        const result = await this.props.api.call({
            method: 'get',
            url: `/api/media/${mediaId}/view`,
        });
        return result.data;
    }

    async removeMedia(file) {
        await this.props.api.call({
            method: 'delete',
            url: `/api/media/${file}`,
        });
        this.props.events.publish(PatientService.EVENTS.patient_updated);
        this.props.events.publish(PatientService.EVENTS.uploads_updated);
        this.props.events.publish(PatientService.EVENTS.notes_updated);
    }

    async addReminder(entity, description, dueAt) {
        const data = {
            ...entity,
            description,
            due_at: dueAt,
        };
        await this.props.api.call({
            method: 'post',
            url: '/api/tasks',
            data,
        });
        this.props.events.publish(PatientService.EVENTS.reminders_updated);
    }

    async editReminder(reminder, data, delayPublish) {
        await this.props.api.call({
            method: 'put',
            url: `/api/tasks/${reminder.id}`,
            data,
        });
        if (!delayPublish) {
            this.props.events.publish(PatientService.EVENTS.reminders_updated);
            this.getGap(
                reminder.risk_gap?.id ?? reminder.foc_gap?.id ?? reminder.quality_gap?.id,
                reminder.type
            );
        }
    }

    async getReminders(entity, status) {
        const statusUrl = status
            ? `task_status[]=${enumValue([
                  'task',
                  'status',
                  'Completed',
              ])}&task_status[]=${enumValue([
                  'task',
                  'status',
                  'Dismissed',
              ])}&task_status[]=${enumValue(['task', 'status', 'Missed'])}`
            : `task_status[]=${enumValue([
                  'task',
                  'status',
                  'Not Started',
              ])}&task_status[]=${enumValue(['task', 'status', 'In Progress'])}`;
        const result = await this.props.api.call({
            method: 'get',
            url: `/api/tasks?${entity}&${statusUrl}`,
            data: {},
            headers: {
                'Content-Type': 'application/ld+json',
            },
        });
        return {
            results: result.data['hydra:member'],
            pagination: convertPagination(result.data),
        };
    }

    async getHealthHistory(gmpi, type) {
        // Determine url path based on type
        let path = '';
        // Determine which property to use on the response, then make sure it has index values
        let val = `member${type}`;
        if (type === healthHxTabs.labs) {
            path = 'labs';
        }
        if (type === healthHxTabs.meds) {
            path = 'medications';
            val = 'memberMedication';
        }
        if (type === healthHxTabs.specVisits) {
            path = 'specialty-visits';
            val = 'memberSpecialtyVisits';
        }
        if (type === healthHxTabs.diagnosis) {
            path = 'diagnosis';
        }
        if (type === healthHxTabs.vitals) {
            path = 'vitals';
            val = 'memberVital';
        }
        if (type === healthHxTabs.inptOutpt) {
            path = 'in-out-patients';
            val = 'memberInOutPatient';
        }

        const result = await this.props.api.call(
            {
                method: 'get',
                url: `/wellmed/api/gmpi/${gmpi}/${path}`,
            },
            { handledErrors: true }
        );

        // On empty results, the API sends back this error code, so convert to an empty array
        if (result.data?.errorCode === 'NoResultsFound') return [];

        return result.data.data[val].map((x, i) => ({ ...x, id: i }));
    }

    async getICDCodes(search) {
        const result = await this.props.api.call({
            method: 'get',
            url: `/api/icd-codes?code=${search}`,
        });
        return result.data['hydra:member'];
    }

    async editFilename(id, data) {
        await this.props.api.call({
            method: 'put',
            url: `/api/media/${id}`,
            data,
        });
        this.props.events.publish(PatientService.EVENTS.uploads_updated);
        this.props.events.publish(PatientService.EVENTS.notes_updated);
    }
}

export { PatientService };
